import ErrorHandlerHelpers from '../../mixins/ErrorHandlerHelpers';
import PassengerService from '../../services/PassengerService';
import Idb from '../../services/IndexedDbService';
import getPassengerStatus from '../passengerHelpers';

const SET_PASSENGERS = 'SET_PASSENGERS';
const ADD_PASSENGER = 'ADD_PASSENGER';
const REMOVE_PASSENGER = 'REMOVE_PASSENGER';
const CLEAR_PASSENGERS = 'CLEAR_PASSENGERS';

const state = {
  passengers: [],
};

const getters = {
  getPassengers: () => state.passengers,
  getSyncedAndUnsyncedPassengersForVoyage:
    (state, getters, rootState) => (voyageReportId) => {
      const unsyncedPassengers = rootState.dataSyncStore.passengersToBeSynced;
      const unsyncedPassengersForVoyage = unsyncedPassengers
        .filter((x) => x.voyageReportId === voyageReportId
          || x.voyageReportIdc === voyageReportId);

      // Filter outdated synced passengers (passengers that have been updated while offline)
      const syncedIdFilter = unsyncedPassengers.map((x) => x.id);
      const filteredSyncedPassengers = state.passengers
        .filter((x) => !syncedIdFilter.includes(x.id));

      const result = []
        .concat(unsyncedPassengersForVoyage || [])
        .concat(filteredSyncedPassengers);

      return result;
    },
};

const mutations = {
  [SET_PASSENGERS](state, passengers) {
    state.passengers = passengers;
  },
  [ADD_PASSENGER](state, passenger) {
    state.passengers.push(passenger);
  },
  [REMOVE_PASSENGER](state, passengerId) {
    state.passengers = state.passengers.filter((x) => x.id !== passengerId);
  },
  [CLEAR_PASSENGERS](state) {
    state.passengers = [];
  },
};

const actions = {
  loadPassengersForVoyage({ commit, dispatch }, voyageReportId) {
    return PassengerService.getPassengersForVoyage(voyageReportId)
      .then((x) => x.data)
      .then((passengers) => commit(SET_PASSENGERS, passengers))
      .then(dispatch('dataSyncStore/loadPassengersToBeSynced', null, { root: true }))
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  createPassenger({ commit, rootState, dispatch }, newPassengerModel) {
    const online = rootState.userStore.isClientConnectedToServer;
    if (online) {
      return PassengerService.createPassenger(newPassengerModel)
        .then((x) => x.data)
        .then((resp) => {
          const createdPassenger = resp.entity;
          commit(ADD_PASSENGER, createdPassenger);
          return resp;
        })
        .catch(ErrorHandlerHelpers.handleServiceError);
    }

    // Set status
    const newPassengerIdbModel = { ...newPassengerModel };
    newPassengerIdbModel.status = getPassengerStatus(newPassengerModel);

    return Idb.savePassenger(newPassengerIdbModel)
      .then((createdPassengerIdc) => Idb.getPassengerByIdc(createdPassengerIdc)
        .then((passenger) => {
          commit('dataSyncStore/ADD_PASSENGER_TO_BE_SYNCED', passenger, { root: true });
          const result = { message: 'Passenger Created' };
          return result;
        })).catch(ErrorHandlerHelpers.handleServiceError);
  },
  updatePassenger({ dispatch, rootState }, updatePassengerModel) {
    const online = rootState.userStore.isClientConnectedToServer;
    if (online && updatePassengerModel.id && !updatePassengerModel.idc) {
      return PassengerService.updatePassenger(updatePassengerModel)
        .then((x) => x.data)
        .then((resp) => {
          const voyageReportId = resp.entity.voyageReport.id;
          dispatch('loadPassengersForVoyage', voyageReportId);
          return resp;
        }).catch(ErrorHandlerHelpers.handleServiceError);
    }

    // Set status
    const updatePassengerIdbModel = { ...updatePassengerModel };
    updatePassengerIdbModel.status = getPassengerStatus(updatePassengerModel);

    return Idb.updatePassenger(updatePassengerIdbModel).then(() => {
      dispatch('loadPassengersForVoyage', updatePassengerModel.voyageReportId);
      const result = { message: 'Passenger Created' };
      return result;
    }).catch(ErrorHandlerHelpers.handleServiceError);
  },
  deletePassenger({ commit, rootState, dispatch }, passenger) {
    const online = rootState.userStore.isClientConnectedToServer;
    const { id, idc } = passenger;
    if (online && id && !idc) {
      return PassengerService.deletePassenger(id)
        .then((x) => x.data)
        .then((result) => {
          if (result.success) commit(REMOVE_PASSENGER, id);
          return result;
        })
        .catch(ErrorHandlerHelpers.handleServiceError);
    }

    return Idb.deletePassenger(passenger).then(() => {
      dispatch('loadPassengersForVoyage', passenger.voyageReportId);
      const result = { message: 'Passenger Deleted' };
      return result;
    }).catch(ErrorHandlerHelpers.handleServiceError);
  },
  clearPassengers({ commit }) {
    commit(CLEAR_PASSENGERS);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
